// ============================================================
// WindowModal.jsx  —  Noble Enterprises (tape module)
//
// A modal that behaves like a Windows window: minimise,
// maximise, close. Minimised windows collapse to buttons in a
// taskbar along the bottom of the screen, and clicking one
// brings it back exactly as it was.
//
// WHY A PROVIDER
// A minimised window has to survive after the modal itself
// stops rendering, and the taskbar has to sit outside every
// individual modal. So the list of minimised windows lives in
// a context at the top of the module, and the taskbar renders
// there once.
//
// NO OUTSIDE-CLICK CLOSE
// Clicking the backdrop does nothing. Half-filled forms were
// being lost by a stray click outside them.
//
// HOW THIS FILE IS ORGANISED
//   A. Context + provider
//   B. Taskbar
//   C. WindowModal
//   D. Shared date defaults
// ============================================================

import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, Minus, Square, Copy } from 'lucide-react';

const C = {
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  brand:      '#f39c12',
  sidebarBg:  '#1a2332',
  danger:     '#dc2626',
};


// ============================================================
// D. DATE DEFAULTS
//
// Every date range in the module starts here, so they can't
// drift apart screen by screen.
// ============================================================

export const DEFAULT_FROM_DATE = '2025-01-01';
export const todayISO = () => new Date().toISOString().split('T')[0];


// ============================================================
// A. CONTEXT + PROVIDER
// ============================================================

const WindowContext = createContext(null);

export const useWindowManager = () => {
  const ctx = useContext(WindowContext);
  // Falling back rather than throwing means a modal still works
  // if it is ever rendered outside the provider — it just loses
  // the minimise button.
  return ctx || { minimised: [], minimise: () => {}, restore: () => {}, close: () => {} };
};

export const WindowManagerProvider = ({ children }) => {
  // [{ id, title }] — one entry per minimised window
  const [minimised, setMinimised] = useState([]);

  const minimise = useCallback((id, title) => {
    setMinimised(prev =>
      prev.some(w => w.id === id) ? prev : [...prev, { id, title }]
    );
  }, []);

  const restore = useCallback((id) => {
    setMinimised(prev => prev.filter(w => w.id !== id));
  }, []);

  const close = useCallback((id) => {
    setMinimised(prev => prev.filter(w => w.id !== id));
  }, []);

  return (
    <WindowContext.Provider value={{ minimised, minimise, restore, close }}>
      {children}
      <Taskbar />
    </WindowContext.Provider>
  );
};


// ============================================================
// B. TASKBAR
//
// Only appears when something is minimised, so it costs no
// screen space the rest of the time.
// ============================================================

const Taskbar = () => {
  const { minimised, restore } = useWindowManager();

  if (minimised.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0,
      background: C.sidebarBg,
      borderTop: `1px solid rgba(255,255,255,0.12)`,
      padding: '8px 14px',
      display: 'flex', alignItems: 'center', gap: '8px',
      flexWrap: 'wrap',
      zIndex: 1200,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
    }}>
      {minimised.map(w => (
        <button
          key={w.id}
          onClick={() => restore(w.id)}
          title={`Restore ${w.title}`}
          style={{
            display: 'flex', alignItems: 'center', gap: '9px',
            padding: '8px 14px', borderRadius: '6px',
            background: 'rgba(255,255,255,0.08)',
            border: `1px solid rgba(255,255,255,0.14)`,
            borderBottom: `2px solid ${C.brand}`,
            color: '#dde4ed', fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', fontFamily: 'inherit',
            maxWidth: '240px',
          }}
        >
          <Square size={11} strokeWidth={2.5} style={{ color: C.brand, flexShrink: 0 }} />
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {w.title}
          </span>
        </button>
      ))}
    </div>
  );
};


// ============================================================
// C. WINDOW MODAL
//
// Props
//   id        stable string, used by the taskbar
//   title     shown in the title bar and on the taskbar button
//   subtitle  optional line under the title
//   onClose   called by the X button only
//   width     normal width; ignored when maximised
// ============================================================

const WindowModal = ({ id, title, subtitle, onClose, width = '760px', children }) => {
  const { minimised, minimise, restore, close } = useWindowManager();

  const [maximised, setMaximised] = useState(false);

  const isMinimised = minimised.some(w => w.id === id);

  const handleClose = () => {
    close(id);
    onClose();
  };

  // Minimised windows stay mounted but hidden, so form state
  // and scroll position survive being put away and brought back.
  const hidden = isMinimised;

  const btnStyle = (danger) => ({
    width: '30px', height: '30px', borderRadius: '6px',
    border: 'none', background: 'transparent',
    color: danger ? C.textSoft : C.textSoft,
    cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    fontFamily: 'inherit',
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: hidden ? 'none' : 'flex',
        alignItems: maximised ? 'stretch' : 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: maximised ? '0' : '20px',
      }}
      // Deliberately no onClick — a stray click outside a
      // half-filled form used to throw the work away.
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: maximised ? '0' : '14px',
          width: maximised ? '100%' : width,
          maxWidth: '100%',
          height: maximised ? '100%' : 'auto',
          maxHeight: maximised ? '100%' : '88vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: maximised ? 'none' : '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Title bar */}
        <div style={{
          padding: maximised ? '16px 20px' : '18px 20px 18px 24px',
          borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'flex-start', gap: '12px',
          flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              margin: 0, fontSize: '17px', fontWeight: '700', color: C.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {title}
            </h3>
            {subtitle && (
              <p style={{
                margin: '3px 0 0', fontSize: '13px', color: C.textSoft,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Window buttons, in Windows order: minimise, maximise, close */}
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            <button
              onClick={() => minimise(id, title)}
              title="Minimise"
              style={btnStyle()}
              onMouseEnter={e => { e.currentTarget.style.background = C.borderSoft; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Minus size={16} />
            </button>

            <button
              onClick={() => setMaximised(m => !m)}
              title={maximised ? 'Restore down' : 'Maximise'}
              style={btnStyle()}
              onMouseEnter={e => { e.currentTarget.style.background = C.borderSoft; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {maximised ? <Copy size={14} /> : <Square size={14} />}
            </button>

            <button
              onClick={handleClose}
              title="Close"
              style={btnStyle(true)}
              onMouseEnter={e => {
                e.currentTarget.style.background = C.danger;
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = C.textSoft;
              }}
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{
          padding: maximised ? '22px 28px' : '22px 24px',
          overflowY: 'auto',
          flex: maximised ? 1 : 'initial',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default WindowModal;